import type { RootState } from "~/app/store";
import { queryClient } from "~/app/queryClient";
import { sessionQueryOptions } from "~/app/api/pageData";
import { fetchContentSearchResults } from "../api/fetchContentSearchResults";
import { redirectToUrl } from "../../../utils/url/urlUtils";
import { initiallyExpandCategory } from "../utils/seriesTreeUtils";
import { isComingFromSeriesTreeClick } from "./seriesTreeSelectors";
import { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import instance from "../../../utils/i18n";
import { filterUnavailableParams } from "~/features/search/utils/filterUnavailableParams";
import { fetchSearchContext } from "~/domains/search/api/searchContext";
import { fetchRecommendedProductsSearch } from "~/features/search/api/fetchRecommendedProductsSearch";
import { transformSrpProduct } from "~/features/search/transformers/transformSrpProduct";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import { uniqueValues } from "~/common/utils/misc";
import { getSearchPageType } from "~/features/search/utils/searchPageUtils";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import type { SearchPageState } from "~/features/search/redux/types";
import { SelectionAssistantType } from "~/features/search/models/SelectionAssistantType";
import { fetchSearchResults } from "~/features/search/api/fetchSearchResults";
import {
  type BreadcrumbData,
  BreadcrumbType,
  type ContentDataItem,
  type PaginationData,
  type SearchResultData,
  type SrpProductData,
  type TechValueBreadcrumb,
  type FacetBreadcrumb,
} from "~/utils/models";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { addMessage } from "~/features/toasts/redux/actions";
import { type AnyAction } from "redux";
import { type SortDirection } from "~/features/search/models/models";
import { type ThunkAction } from "redux-thunk";
import {
  convertProductToImpression,
  sendCharacteristicsUsage,
  sendSearchSortChoice,
  sendSrpProductClick,
  sendSrpProductListImpression,
  trackSelectionAssistantRotaryFilters,
} from "~/app/analytics/ga/ga";
import { type ActionType } from "~/app/store/types";
import {
  transformContentSearchResults,
  transformSearchResults,
} from "~/features/search/transformers/SearchResultDataTransformer";
import {
  type FromUrlSearchParams,
  generateDataFromUrl,
  generateUrlFromStore,
} from "~/features/search/utils/historyManipulation";
import { selectHasNonDefaultSort } from "~/features/search/redux/searchSelectors";
import { getSelectedProductGroupFromSearchResults } from "~/features/search/utils/searchUtils";
import { Filter, type FacetFilterData } from "~/features/search/models/filterConfigModels";
import { UnitType } from "../api/fetchFilterConfig";

import {
  UPDATE_SEARCH_PARAMS_FROM_URL,
  SEARCH_START,
  SEARCH_SUCCESS,
  CONTENT_SEARCH_SUCCESS,
  SEARCH_FAILURE,
  CHANGE_SORT,
  CHANGE_SORT_DIRECTION,
  CHANGE_PAGE,
  CHANGE_CONTENT_PAGE,
  TOGGLE_FACET,
  REMOVE_BREADCRUMB,
  UPDATE_SEARCH_TEXT,
  CLEAR_ALL,
  APPLY_ALL,
  CLEAR_TECH_VALUES,
  SELECT_PRODUCT_GROUP,
  SELECT_PRODUCT_GROUP_MULTI,
  SET_SELECTED_PRODUCT_GROUPS,
  UPDATE_ASSISTANT_VALUES,
  UPDATE_IS_SELECTION_ASSISTANT,
  UPDATE_CAMPAIGN_FILTER,
  UPDATE_RELEVANT_FILTER,
  SET_SHOW_RELEVANT_TOGGLE,
  SRP_FETCH_RECOMMENDED_PRODUCTS_START,
  SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
  SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE,
  SET_RECOMMENDATION_PLACEMENT,
  UPDATE_COLUMN_VISIBILITY,
} from "./actionTypes";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type AsyncThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, AnyAction>;

export function searchHistoryChanged(pageType: SearchPageType): ThunkResult<void> {
  return (dispatch, getState) => {
    const isLoading = getState().srp.meta.inFlight;
    if (!isLoading) {
      dispatch(searchResultsMounted());
    }
  };
}

export function handleMissingSortAfterTabChangeThunk(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().srp;
    const currentSort = state.sort.selectedCode;
    if (!state.sort.availableSorts.find((sort) => sort.code === currentSort)) {
      dispatch(changeSort("relevance"));
    }
  };
}

function toggleCampaignOnlyResults(enabled: boolean) {
  return {
    type: UPDATE_CAMPAIGN_FILTER,
    payload: enabled,
  } as const;
}

function updateRelevantFilterAction(enabled: boolean) {
  return {
    type: UPDATE_RELEVANT_FILTER,
    payload: enabled,
  } as const;
}

function setShowRelevantToggleAction(showRelevantToggle: boolean) {
  return {
    type: SET_SHOW_RELEVANT_TOGGLE,
    payload: showRelevantToggle,
  } as const;
}

export const toggleCampaignOnlyResultsThunk = (enabled: boolean) =>
  searchAfterwards(toggleCampaignOnlyResults(enabled));
export const updateRelevantFilterThunk = (enabled: boolean) => searchAfterwards(updateRelevantFilterAction(enabled));

/**
 * Resolves the current user's valid facet-suffix list from the search-context OCC
 * resource. Always returns a fresh value on each search mount so an account change is
 * reflected immediately;
 * falls back to an empty string if the resource is unavailable.
 */
async function resolveValidSearchSuffixes(): Promise<string> {
  try {
    return (await fetchSearchContext()).validSearchSuffixes;
  } catch {
    // Search context unavailable; empty string mirrors an empty config value.
  }
  return "";
}

async function resolveZoovuEnabled(): Promise<boolean> {
  try {
    const { commerceContext } = await queryClient.fetchQuery(sessionQueryOptions());
    return Boolean(commerceContext.zoovuEnabled);
  } catch {
    return false;
  }
}

export function salesCampaignMounted(showRelevantToggle: boolean): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(setShowRelevantToggleAction(showRelevantToggle));
    const validSearchSuffixes = await resolveValidSearchSuffixes();
    const zoovuEnabled = await resolveZoovuEnabled();
    let urlData = generateDataFromUrl();
    urlData = filterUnavailableParams(urlData, validSearchSuffixes, zoovuEnabled);
    dispatch(updateStateFromUrl(urlData));
    const isProductGroupSelected = getState().srp.facets.selectedProductGroups.length > 0;

    await dispatch(runSearch(true, !isProductGroupSelected, false));
  };
}

export function searchResultsMounted(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const validSearchSuffixes = await resolveValidSearchSuffixes();
    const zoovuEnabled = await resolveZoovuEnabled();
    let urlData = generateDataFromUrl();
    urlData = filterUnavailableParams(urlData, validSearchSuffixes, zoovuEnabled);
    dispatch(updateStateFromUrl(urlData));
    dispatch(loadCartAmounts());

    AnalyticsMQ.instance.push("searchPageOpen", {});

    // SRP-scoped: PLP mounts dispatch this thunk too, but the redirect=true arrival
    // toast only describes a bounce back onto the search page.
    if (
      getSearchPageType() === SearchPageType.SEARCH &&
      new URLSearchParams(window.location.search).get("redirect") === "true"
    ) {
      dispatch(addMessage("info", "redirect.to.search.page.message", "translation"));
    }

    const isProductGroupSelected = getState().srp.facets.selectedProductGroups.length > 0;

    await dispatch(runSearch(true, !isProductGroupSelected, false));
  };
}

export function selectionAssistantOnMount(): ThunkResult<void> {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_IS_SELECTION_ASSISTANT,
      payload: {
        value: SelectionAssistantType.ROTARY,
      },
    });

    // // Why was this here?
    // await dispatch(runSearch(false, false, url));
    dispatch(updateStateFromUrl(generateDataFromUrl()));

    dispatch(loadCartAmounts());

    await dispatch(runSearch(true, false, false));
  };
}

export function runSearch(
  firstTime: boolean | undefined,
  preselectCategories: boolean | undefined,
  ignoreContent: boolean, // don't update content results
): AsyncThunkResult<SearchResultData | string> {
  return async (dispatch, getStore) => {
    const state = getStore();

    if (!firstTime) {
      window.history.replaceState({}, "", generateUrlFromStore(state));
    }

    dispatch(searchStart());

    try {
      const response = await fetchSearchResults(state.srp, preselectCategories);
      if (response) {
        if (response.keywordRedirectUrl) {
          dispatch(addMessage("info", "redirect.to.page.default.message", "translation"));
          redirectToUrl(response.keywordRedirectUrl);
          return "redirected";
        }
        if (response.pdpRedirectUrl) {
          redirectToUrl(response.pdpRedirectUrl);
          return "redirected";
        }
        const pageType = getSearchPageType();
        const showRelevantToggle = pageType === SearchPageType.Campaign && state.srp.searchParams.showRelevantToggle;
        const zoovuEnabled = await resolveZoovuEnabled();
        const data = transformSearchResults(response, pageType, showRelevantToggle, zoovuEnabled);

        dispatch(searchSuccess(data, preselectCategories || false, ignoreContent));
        window.history.replaceState({}, "", generateUrlFromStore(getStore()));

        const currentState = getStore();
        const isProductListPage = pageType === SearchPageType.SEARCH || pageType === SearchPageType.PLP;
        if (isProductListPage && currentState.srp.recommendationPlacement.placement?.type === "SALES_CAMPAIGN") {
          if (currentState.srp.searchParams.currentPage === 0) {
            dispatch(fetchSRPCampaignRecommendationsThunk());
          }
        }

        return data;
      } else {
        const msg = `An error occurred`;
        dispatch(searchFailure(msg));
        return msg;
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        return "";
      }
      console.error(e);
      const msg = "A timeout has occured";
      dispatch(searchFailure(msg));
      return msg;
    }
  };
}

export const setRecommendationPlacement = SET_RECOMMENDATION_PLACEMENT;

export function fetchSRPCampaignRecommendationsThunk(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    if (!userIsCustomer()) {
      return; // Feature only for customers
    }

    if (!window.SCHA.activeSalesCampaigns.length) {
      return; // There's no campaigns active, so no way there can be recommendations
    }

    dispatch(startFetchingSRPCampaignRecommendations());

    try {
      const data = await fetchRecommendedProductsSearch(state.srp);
      const products = data.map(transformSrpProduct);
      dispatch(fetchSRPCampaignRecommendationsSuccess(products));
    } catch (error) {
      dispatch(fetchSRPCampaignRecommendationsFailure());
    }
  };
}

function startFetchingSRPCampaignRecommendations() {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_START,
  } as const;
}

export function fetchSRPCampaignRecommendationsSuccess(products: SrpProductData[]) {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
    payload: products,
  } as const;
}

function fetchSRPCampaignRecommendationsFailure() {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE,
  } as const;
}

export function searchAfterwards(action: ActionType<unknown>, ignoreContent: boolean = false): ThunkResult<void> {
  return (dispatch) => {
    dispatch(action);
    // TODO: Make it better
    if (!window.location.href.includes("housingselectionassistant")) {
      dispatch(runSearch(false, false, ignoreContent));
    }
  };
}

function searchStart(): ActionType<void> {
  trackSelectionAssistantRotaryFilters();
  return {
    type: SEARCH_START,
  };
}

export function contentSearchSuccess(data: { results: ContentDataItem[]; pagination: PaginationData }) {
  return {
    type: CONTENT_SEARCH_SUCCESS,
    payload: data,
  } as const;
}

export function searchSuccess(
  data: SearchResultData,
  preselectCategories: boolean,
  ignoreContent?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    let selectedProductGroups: string[];
    if (preselectCategories) {
      const group = getSelectedProductGroupFromSearchResults(data);
      selectedProductGroups = group ? [group] : [];
    } else {
      selectedProductGroups = getState().srp.facets.selectedProductGroups;
    }
    if (data.seriesTree) {
      initiallyExpandCategory(data.seriesTree, selectedProductGroups);
    }

    const srp = getState().srp;
    AnalyticsMQ.instance.push("searchSuccess", {
      resultAmount: data.pagination.totalNumberOfResults,
      query: srp.searchParams.text,
      pageNumber: srp.searchParams.currentPage,
      pageSize: srp.results.pagination.pageSize,
      webpageType: getSearchPageType(),
    });

    dispatch(searchSuccessAction(data, selectedProductGroups, ignoreContent));
  };
}

export function searchSuccessAction(data: SearchResultData, selectedCategories: string[], ignoreContent?: boolean) {
  return {
    type: SEARCH_SUCCESS,
    payload: {
      data,
      selectedCategories,
      ignoreContent,
    },
  } as const;
}

function searchFailure(error: string) {
  return {
    type: SEARCH_FAILURE,
    payload: error,
  };
}

export function updateStateFromUrl(data: FromUrlSearchParams) {
  return {
    type: UPDATE_SEARCH_PARAMS_FROM_URL,
    payload: data,
  } as const;
}

export const changePage = (pageNumber: number) => searchAfterwards({ type: CHANGE_PAGE, payload: pageNumber }, true);

export const changeContentResultsPage = (pageNumber: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    dispatch({
      type: CHANGE_CONTENT_PAGE,
      payload: pageNumber,
    });
    const state = getState();
    const query = state.srp.searchParams.text;

    try {
      const response = await fetchContentSearchResults(query, pageNumber);
      if (response) {
        const data = transformContentSearchResults(response);
        dispatch(contentSearchSuccess(data));
        return data;
      } else {
        const msg = `An error occurred`;
        dispatch(searchFailure(msg));
        return msg;
      }
    } catch (e) {
      const msg = "A timeout has occured";
      dispatch(searchFailure(msg));
      return msg;
    }
  };
};

export const changeSort = (sortCode: string, direction?: SortDirection) => {
  sendSearchSortChoice(sortCode);
  return searchAfterwards(changeSortAction(sortCode, direction), true);
};

export function changeSortAction(sortCode: string, direction?: SortDirection) {
  return {
    type: CHANGE_SORT,
    payload: { sortCode, direction },
  } as const;
}

export const changeSortDirection = (direction: SortDirection) =>
  searchAfterwards(changeSortDirectionAction(direction), true);

export function changeSortDirectionAction(direction: SortDirection) {
  return {
    type: CHANGE_SORT_DIRECTION,
    payload: direction,
  } as const;
}

export interface FacetToggleData {
  newValue: boolean;
  facetValueCode: string;
  facetCode: string;
  name: string;
  value: string;
}

export function toggleFacet(facetValueCode: string, facetCategoryCode: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const facet = getState().srp.facets.facetValues[facetCategoryCode];
    const facetValue = facet.values.filter((item) => item.code === facetValueCode)[0];
    let fullName;
    if (facet.code === "productSpecific") {
      fullName = `${instance.t(facetValue.name)}`;
    } else {
      fullName = `${facet.name}: ${facetValue.name}`;
    }

    const hasBreadcrumb = state.srp.facets.breadcrumbs.filter(
      (bc) => bc.type === BreadcrumbType.FACET && bc.facetValueCode === facetValueCode,
    )[0];
    const newValue = !hasBreadcrumb;
    dispatch({
      type: TOGGLE_FACET,
      payload: {
        newValue,
        facetValueCode,
        facetCode: facet.code,
        name: facet.name,
        value: facetValue.name,
      },
    });
    dispatch(runSearch(false, false, true));

    if (newValue) {
      sendCharacteristicsUsage([facetValueCode]);
    }
  };
}

function removeBreadcrumbInternal(breadcrumb: BreadcrumbData): ActionType<BreadcrumbData> {
  return {
    type: REMOVE_BREADCRUMB,
    payload: breadcrumb,
  };
}

export const removeBreadcrumb = (breadcrumb: BreadcrumbData) =>
  searchAfterwards(removeBreadcrumbInternal(breadcrumb), true);

export function clearAll(searchPageType: SearchPageType): ThunkResult<void> {
  return (dispatch, getState) => {
    const isFromSeriesTree = isComingFromSeriesTreeClick(getState());
    if (!isFromSeriesTree) {
      dispatch(resetProductGroupToLevel1Internal());
    }
    dispatch({
      type: CLEAR_ALL,
      payload: { searchPageType },
    });
    dispatch(runSearch(false, undefined, true));
  };
}

export function clearSearchValueAction(pageType: SearchPageType): ThunkResult<void> {
  return (dispatch) => {
    dispatch(updateSearchText(""));
  };
}

function isTechValueBreadcrumb(bc: BreadcrumbData): bc is TechValueBreadcrumb {
  return bc.type === BreadcrumbType.TECH_VALUE;
}

export function clearAllTechFilters(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const codesToClear = uniqueValues(state.srp.facets.breadcrumbs.filter(isTechValueBreadcrumb).map((bc) => bc.code));

    dispatch(clearTechFilters(codesToClear));
  };
}

/**
 * Clears all tech values if not supplied a filter
 * @param techValueCategoryTitles
 */

export function clearTechFilters(techFilterUids: string | string[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const codesToClear = Array.isArray(techFilterUids) ? techFilterUids : [techFilterUids];
    if (!codesToClear.length) {
      return;
    }
    const breadCrumbsToClear = getState().srp.facets.breadcrumbs.filter(
      (bc) => bc.type === BreadcrumbType.TECH_VALUE && codesToClear.indexOf(bc.code) !== -1,
    );
    dispatch({ type: CLEAR_TECH_VALUES, payload: codesToClear });
    breadCrumbsToClear.forEach((bc) => {
      dispatch(removeBreadcrumbInternal(bc));
    });
    dispatch(runSearch(false, false, true));
  };
}

export function applyAllFiltersAction(values: Record<string, number | undefined>): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const unitMode = state.userPreferences.unitMode;

    dispatch({
      type: APPLY_ALL,
      payload: {
        unitMode,
        values,
      },
    });

    dispatch(runSearch(false, false, true));
  };
}

export function updateSearchText(searchText: string): ThunkResult<void> {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_SEARCH_TEXT,
      payload: { searchText, searchPageType: getSearchPageType() },
    });

    await dispatch(runSearch(false, true, false));
  };
}

function getActiveCharacteristicGroups(state: SearchPageState) {
  const facetCodes = Object.keys(state.facets.facetValues);
  const activeFacetCodes = facetCodes.filter((code) =>
    state.facets.breadcrumbs.some((breadcrumb) => {
      if (breadcrumb.type === BreadcrumbType.FACET) {
        return breadcrumb.facetCode === code;
      }
      return false;
    }),
  );
  return activeFacetCodes;
}

function areValueFiltersActive(state: SearchPageState) {
  return state.facets.breadcrumbs.some((bc) => bc.type === BreadcrumbType.TECH_VALUE);
}

export function searchItemClick(area: string, product: SrpProductData): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const srp = state.srp;
    const hasValueFilterActive = areValueFiltersActive(srp);
    const hasCharacteristics = getActiveCharacteristicGroups(srp).length > 0;
    const hasFilteringActive = hasValueFilterActive || hasCharacteristics;

    const hasNonDefaultSort = selectHasNonDefaultSort(state);

    const position =
      1 +
      srp.results.results.findIndex((row) => row.productCode === product.code) +
      srp.results.pagination.pageSize * srp.searchParams.currentPage;

    sendSrpProductClick(product, hasFilteringActive, hasNonDefaultSort, area, position);
  };
}

export function searchAfterScroll(oldLastSeen: number, newLastSeen: number): ThunkResult<void> {
  return (dispatch, getState) => {
    // We didn't see anything new - no impressions to send
    if (oldLastSeen >= newLastSeen) {
      return;
    }

    const state = getState();
    const srp = state.srp;
    const basicProducts = state.products.basic;
    const productPrices = state.products.price;
    const productAvailabilities = state.products.availability;
    const hasValueFilterActive = areValueFiltersActive(srp);
    const hasCharacteristics = getActiveCharacteristicGroups(srp).length > 0;
    const hasFilteringActive = hasValueFilterActive || hasCharacteristics;

    const hasNonDefaultSort = selectHasNonDefaultSort(state);

    const searchResultRows = srp.results.results.slice(oldLastSeen, newLastSeen);

    const pageType = getSearchPageType();

    const impressions = searchResultRows.map((row, index) => {
      // TODO: Use a Product Descriptor here?
      const code = row.productCode;
      const product = basicProducts[code];
      const prices = productPrices[code]?.data;
      const price = prices?.length === 1 ? prices[0] : undefined;
      const availability = productAvailabilities[code]?.data ?? undefined;
      const impression = convertProductToImpression({ basic: product, price, availability });
      // TODO - Other cases? Campaign!
      impression.list = pageType === SearchPageType.PLP ? window.SCHA.pageCategory : "Search Results";
      impression.position = 1 + oldLastSeen + index + srp.results.pagination.pageSize * srp.searchParams.currentPage;
      return impression;
    });

    sendSrpProductListImpression(impressions, hasFilteringActive, hasNonDefaultSort);
  };
}

export function selectProductGroupAction(code: string) {
  return {
    type: SELECT_PRODUCT_GROUP,
    payload: {
      code,
    },
  } as const;
}

export function selectProductGroup(code: string = ""): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(selectProductGroupAction(code));
    dispatch(runSearch(false, undefined, true));
  };
}

export function selectProductGroupMultipleAction(code: string) {
  return {
    type: SELECT_PRODUCT_GROUP_MULTI,
    payload: {
      code,
    },
  } as const;
}

export function setSelectedProductGroupsThunk(values: string | string[]): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(setSelectedProductGroups(values));
    dispatch(runSearch(false, undefined, true));
  };
}

export function setSelectedProductGroups(values: string | string[]) {
  return {
    type: SET_SELECTED_PRODUCT_GROUPS,
    payload: {
      values: Array.isArray(values) ? values : [values],
    },
  } as const;
}

export function selectProductGroupWithUrl(code: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(selectProductGroupMultipleAction(code));
    dispatch(runSearch(false, false, true));
  };
}

function resetProductGroupToLevel1Internal(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const selectedProductGroup = state.srp.facets.selectedProductGroups[0] ?? "";
    const newCode = selectedProductGroup
      .split("/")
      .filter((_, i) => i < 3)
      .join("/");

    dispatch(selectProductGroupAction(newCode));
  };
}

export const updateAssistantValues = (code: string): ThunkResult<void> => {
  return async (dispatch) => {
    await dispatch({
      type: UPDATE_ASSISTANT_VALUES,
      payload: {
        code,
      },
    });

    await dispatch(runSearch(false, false, true));
  };
};

export function updateColumnVisibilityAction(
  columnVisibility: { code: string; visible: boolean }[],
  unitMode: UnitMode,
) {
  return {
    type: UPDATE_COLUMN_VISIBILITY,
    payload: { columnVisibility, unitMode },
  } as const;
}

export function clearAllFacetFilters(unitMode: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    let facetFilters: FacetFilterData[] = [];

    if (unitMode === UnitMode.Imperial) {
      facetFilters = state.srp.facets.filterConfigImperial.filter(
        (item): item is FacetFilterData => item.type === Filter.FACET && item.unitType === UnitType.IMPERIAL,
      );
    } else {
      facetFilters = state.srp.facets.filterConfig.filter(
        (item): item is FacetFilterData => item.type === Filter.FACET && item.unitType === UnitType.METRIC,
      );
    }

    const facetFilterCodes = facetFilters.map((item) => item.code);
    const codesToClear = uniqueValues(
      state.srp.facets.breadcrumbs
        .filter(
          (item): item is FacetBreadcrumb =>
            item.type === BreadcrumbType.FACET && facetFilterCodes.includes(item.facetCode),
        )
        .map((bc) => bc.facetCode),
    );
    dispatch(clearFacetFilters(codesToClear));
  };
}

export function clearFacetFilters(facetFilterUids: string | string[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const codesToClear = Array.isArray(facetFilterUids) ? facetFilterUids : [facetFilterUids];
    if (!codesToClear.length) {
      return;
    }
    const breadCrumbsToClear = getState().srp.facets.breadcrumbs.filter(
      (bc) => bc.type === BreadcrumbType.FACET && codesToClear.indexOf(bc.facetCode) !== -1,
    );
    breadCrumbsToClear.forEach((bc) => {
      dispatch(removeBreadcrumbInternal(bc));
    });
    dispatch(runSearch(false, false, true));
  };
}

export {
  UPDATE_SEARCH_PARAMS_FROM_URL,
  SEARCH_START,
  SEARCH_SUCCESS,
  CONTENT_SEARCH_SUCCESS,
  SEARCH_FAILURE,
  CHANGE_SORT,
  CHANGE_SORT_DIRECTION,
  CHANGE_PAGE,
  CHANGE_CONTENT_PAGE,
  TOGGLE_FACET,
  REMOVE_BREADCRUMB,
  UPDATE_SEARCH_TEXT,
  CLEAR_ALL,
  APPLY_ALL,
  CLEAR_TECH_VALUES,
  SELECT_PRODUCT_GROUP,
  SELECT_PRODUCT_GROUP_MULTI,
  SET_SELECTED_PRODUCT_GROUPS,
  UPDATE_ASSISTANT_VALUES,
  UPDATE_IS_SELECTION_ASSISTANT,
  UPDATE_CAMPAIGN_FILTER,
  UPDATE_RELEVANT_FILTER,
  SET_SHOW_RELEVANT_TOGGLE,
  SRP_FETCH_RECOMMENDED_PRODUCTS_START,
  SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
  SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE,
  UPDATE_COLUMN_VISIBILITY,
};
